import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { ConfirmDialog } from "../../admin";

const TitlesTab = ({ clubs = [], players = [], coaches = [] }) => {
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTitleModal, setShowTitleModal] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [titleToDelete, setTitleToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    season: "",
    competition: "",
    dateWon: "",
    description: "",
    clubId: "",
    playerIds: [],
    coachIds: []
  });

  useEffect(() => {
    fetchTitles();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const playersDropdown = document.getElementById('players-dropdown');
      const coachesDropdown = document.getElementById('coaches-dropdown');
      const playersContainer = document.getElementById('players-container');
      const coachesContainer = document.getElementById('coaches-container');

      if (playersDropdown && !playersContainer?.contains(event.target)) {
        playersDropdown.classList.add('hidden');
      }

      if (coachesDropdown && !coachesContainer?.contains(event.target)) {
        coachesDropdown.classList.add('hidden');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchTitles = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/titles`);
      if (!response.ok) throw new Error("Failed to fetch titles");
      const data = await response.json();
      const transformedData = data.map(title => ({
        ...title,
        playerIds: title.winningPlayers?.map(player => player.playerId) || [],
        coachIds: title.winningCoaches?.map(coach => coach.id) || []
      }));
      setTitles(transformedData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTitle = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/titles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error("Failed to add title");
      const newTitle = await response.json();
      setTitles([...titles, newTitle]);
      setShowTitleModal(false);
      setFormData({
        name: "",
        season: "",
        competition: "",
        dateWon: "",
        description: "",
        clubId: "",
        playerIds: [],
        coachIds: []
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTitle = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/titles/${selectedTitle.titleId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...selectedTitle,
            playerIds: selectedTitle.playerIds || [],
            coachIds: selectedTitle.coachIds || [],
            dateWon: selectedTitle.dateWon ? new Date(selectedTitle.dateWon).toISOString().split('T')[0] : null,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to update title");
      const updatedTitle = await response.json();
      const transformedTitle = {
        ...updatedTitle,
        playerIds: updatedTitle.winningPlayers?.map(player => player.playerId) || [],
        coachIds: updatedTitle.winningCoaches?.map(coach => coach.id) || []
      };
      setTitles(titles.map((t) => (t.titleId === transformedTitle.titleId ? transformedTitle : t)));
      setShowTitleModal(false);
      setSelectedTitle(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTitle = async () => {
    if (!titleToDelete) return;
    const title = titleToDelete;

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/titles/${title.titleId}`,
        {
          method: "DELETE",
        }
      );
      if (!response.ok) throw new Error("Failed to delete title");
      setTitles((currentTitles) => currentTitles.filter((t) => t.titleId !== title.titleId));
      setTitleToDelete(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Titles</h2>
        <button
          className="bg-accent hover:bg-accent-hover text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2"
          onClick={() => {
            setSelectedTitle(null);
            setShowTitleModal(true);
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add New Title
        </button>
      </div>

      {error && (
        <div className="bg-accent-dim border-accent/40 text-accent-soft px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Titles Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
        </div>
      ) : (
        <div className="bg-surface-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Title Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Season</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Competition</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Date Won</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Winning Club</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {titles.map((title) => (
                  <tr key={title.titleId} className="hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <span className="text-white font-medium">{title.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300">{title.season}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300">{title.competition}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300">{format(new Date(title.dateWon), 'MMM dd, yyyy')}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-300">{title.winningClub?.name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            const titleToEdit = {
                              ...title,
                              playerIds: title.winningPlayers?.map(player => player.playerId) || [],
                              coachIds: title.winningCoaches?.map(coach => coach.id) || []
                            };
                            setSelectedTitle(titleToEdit);
                            setShowTitleModal(true);
                          }}
                          className="text-gray-400 hover:text-white transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          aria-label={`Delete ${title.name}`}
                          onClick={() => setTitleToDelete(title)}
                          className="text-accent-soft hover:text-accent transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {titleToDelete && (
        <ConfirmDialog
          title="Delete title"
          message={`Delete “${titleToDelete.name}”? This action cannot be undone.`}
          confirmLabel="Delete title"
          onConfirm={handleDeleteTitle}
          onCancel={() => setTitleToDelete(null)}
          busy={loading}
        />
      )}

      {/* Add/Edit Title Modal */}
      {showTitleModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-surface-800 rounded-xl w-full max-w-2xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                {selectedTitle ? "Edit Title" : "Add New Title"}
              </h2>
              <button
                onClick={() => {
                  setShowTitleModal(false);
                  setSelectedTitle(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={selectedTitle ? handleEditTitle : handleAddTitle} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">Title Name</label>
                  <input
                    type="text"
                    value={selectedTitle ? selectedTitle.name : formData.name}
                    onChange={(e) => selectedTitle 
                      ? setSelectedTitle({...selectedTitle, name: e.target.value})
                      : setFormData({...formData, name: e.target.value})}
                    className="w-full bg-surface-700 text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-accent focus:outline-none"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">Season</label>
                  <input
                    type="text"
                    value={selectedTitle ? selectedTitle.season : formData.season}
                    onChange={(e) => selectedTitle
                      ? setSelectedTitle({...selectedTitle, season: e.target.value})
                      : setFormData({...formData, season: e.target.value})}
                    className="w-full bg-surface-700 text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-accent focus:outline-none"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">Competition</label>
                  <input
                    type="text"
                    value={selectedTitle ? selectedTitle.competition : formData.competition}
                    onChange={(e) => selectedTitle
                      ? setSelectedTitle({...selectedTitle, competition: e.target.value})
                      : setFormData({...formData, competition: e.target.value})}
                    className="w-full bg-surface-700 text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-accent focus:outline-none"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">Date Won</label>
                  <input
                    type="date"
                    value={selectedTitle 
                      ? new Date(selectedTitle.dateWon).toISOString().split('T')[0]
                      : formData.dateWon}
                    onChange={(e) => selectedTitle
                      ? setSelectedTitle({...selectedTitle, dateWon: e.target.value})
                      : setFormData({...formData, dateWon: e.target.value})}
                    className="w-full bg-surface-700 text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-accent focus:outline-none"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">Winning Club</label>
                  <select
                    value={selectedTitle ? selectedTitle.clubId : formData.clubId}
                    onChange={(e) => selectedTitle
                      ? setSelectedTitle({...selectedTitle, clubId: e.target.value})
                      : setFormData({...formData, clubId: e.target.value})}
                    className="w-full bg-surface-700 text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-accent focus:outline-none"
                    required
                  >
                    <option value="">Select Club</option>
                    {clubs.map((club) => (
                      <option key={club.clubId} value={club.clubId}>
                        {club.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">Players</label>
                  <div id="players-container" className="relative">
                    <div 
                      className="w-full bg-surface-700 text-white rounded-lg px-4 py-2.5 min-h-[42px] cursor-pointer flex flex-wrap gap-2"
                      onClick={() => {
                        const dropdown = document.getElementById('players-dropdown');
                        if (dropdown) {
                          dropdown.classList.toggle('hidden');
                        }
                      }}
                    >
                      {selectedTitle 
                        ? (selectedTitle.playerIds || []).map(id => {
                            const player = players.find(p => p.playerId === id);
                            return player ? (
                              <span key={id} className="bg-accent-dim px-2 py-1 rounded-md text-sm flex items-center gap-1">
                                {player.name}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTitle({
                                      ...selectedTitle,
                                      playerIds: selectedTitle.playerIds.filter(pid => pid !== id)
                                    });
                                  }}
                                  className="hover:text-white"
                                >
                                  ×
                                </button>
                              </span>
                            ) : null;
                          })
                        : formData.playerIds.map(id => {
                            const player = players.find(p => p.playerId === id);
                            return player ? (
                              <span key={id} className="bg-accent-dim px-2 py-1 rounded-md text-sm flex items-center gap-1">
                                {player.name}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFormData({
                                      ...formData,
                                      playerIds: formData.playerIds.filter(pid => pid !== id)
                                    });
                                  }}
                                  className="hover:text-white"
                                >
                                  ×
                                </button>
                              </span>
                            ) : null;
                          })}
                      {(!selectedTitle?.playerIds?.length && !formData.playerIds.length) && (
                        <span className="text-gray-400">Select players...</span>
                      )}
                    </div>
                    <div 
                      id="players-dropdown"
                      className="absolute top-full left-0 w-full mt-1 bg-surface-700 text-white rounded-lg shadow-lg z-10 hidden max-h-60 overflow-y-auto"
                    >
                      {players.map((player) => (
                        <label 
                          key={player.playerId}
                          className="flex items-center px-4 py-2 hover:bg-surface-700 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedTitle 
                              ? (selectedTitle.playerIds || []).includes(player.playerId)
                              : formData.playerIds.includes(player.playerId)}
                            onChange={(e) => {
                              const currentIds = selectedTitle 
                                ? (selectedTitle.playerIds || [])
                                : formData.playerIds;
                              
                              const newIds = e.target.checked
                                ? [...currentIds, player.playerId]
                                : currentIds.filter(id => id !== player.playerId);

                              if (selectedTitle) {
                                setSelectedTitle({...selectedTitle, playerIds: newIds});
                              } else {
                                setFormData({...formData, playerIds: newIds});
                              }
                            }}
                            className="mr-3 h-4 w-4 text-accent focus:ring-accent border-gray-300 rounded"
                          />
                          <span>{player.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">Coaches</label>
                  <div id="coaches-container" className="relative">
                    <div 
                      className="w-full bg-surface-700 text-white rounded-lg px-4 py-2.5 min-h-[42px] cursor-pointer flex flex-wrap gap-2"
                      onClick={() => {
                        const dropdown = document.getElementById('coaches-dropdown');
                        if (dropdown) {
                          dropdown.classList.toggle('hidden');
                        }
                      }}
                    >
                      {selectedTitle 
                        ? (selectedTitle.coachIds || []).map(id => {
                            const coach = coaches.find(c => c.id === id);
                            return coach ? (
                              <span key={id} className="bg-accent-dim px-2 py-1 rounded-md text-sm flex items-center gap-1">
                                {`${coach.firstName} ${coach.lastName}`}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTitle({
                                      ...selectedTitle,
                                      coachIds: selectedTitle.coachIds.filter(cid => cid !== id)
                                    });
                                  }}
                                  className="hover:text-white"
                                >
                                  ×
                                </button>
                              </span>
                            ) : null;
                          })
                        : formData.coachIds.map(id => {
                            const coach = coaches.find(c => c.id === id);
                            return coach ? (
                              <span key={id} className="bg-accent-dim px-2 py-1 rounded-md text-sm flex items-center gap-1">
                                {`${coach.firstName} ${coach.lastName}`}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFormData({
                                      ...formData,
                                      coachIds: formData.coachIds.filter(cid => cid !== id)
                                    });
                                  }}
                                  className="hover:text-white"
                                >
                                  ×
                                </button>
                              </span>
                            ) : null;
                          })}
                      {(!selectedTitle?.coachIds?.length && !formData.coachIds.length) && (
                        <span className="text-gray-400">Select coaches...</span>
                      )}
                    </div>
                    <div 
                      id="coaches-dropdown"
                      className="absolute top-full left-0 w-full mt-1 bg-surface-700 text-white rounded-lg shadow-lg z-10 hidden max-h-60 overflow-y-auto"
                    >
                      {Array.isArray(coaches) && coaches.length > 0 ? (
                        coaches.map((coach) => (
                          <label 
                            key={coach.id}
                            className="flex items-center px-4 py-2 hover:bg-surface-700 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedTitle 
                                ? (selectedTitle.coachIds || []).includes(coach.id)
                                : formData.coachIds.includes(coach.id)}
                              onChange={(e) => {
                                const currentIds = selectedTitle 
                                  ? (selectedTitle.coachIds || [])
                                  : formData.coachIds;
                                
                                const newIds = e.target.checked
                                  ? [...currentIds, coach.id]
                                  : currentIds.filter(id => id !== coach.id);

                                if (selectedTitle) {
                                  setSelectedTitle({...selectedTitle, coachIds: newIds});
                                } else {
                                  setFormData({...formData, coachIds: newIds});
                                }
                              }}
                              className="mr-3 h-4 w-4 text-accent focus:ring-accent border-gray-300 rounded"
                            />
                            <span>{`${coach.firstName} ${coach.lastName}`}</span>
                          </label>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-gray-400">No coaches available</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">Description</label>
                <textarea
                  value={selectedTitle ? selectedTitle.description : formData.description}
                  onChange={(e) => selectedTitle
                    ? setSelectedTitle({...selectedTitle, description: e.target.value})
                    : setFormData({...formData, description: e.target.value})}
                  className="w-full bg-surface-700 text-white rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-accent focus:outline-none"
                  rows="3"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowTitleModal(false);
                    setSelectedTitle(null);
                  }}
                  className="px-4 py-2 bg-surface-700 text-white rounded-lg hover:bg-surface-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover transition-colors"
                >
                  {selectedTitle ? "Update Title" : "Add Title"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TitlesTab; 